/**
 * Cineamo's own GA4 measurement ID, tracked on every movie landing page regardless of the
 * per-domain configuration (still consent-gated). Leave empty to disable.
 */
export const CINEAMO_GTAG_ID = 'G-ZWJ68LJ5JS';

/**
 * Mapping of internal analytics event names to Google Analytics 4 event names.
 * GA4 custom event names must be <= 40 characters, snake_case.
 * `share` and `video_start` are GA4 recommended events.
 */
export const GA4_EVENT_MAP: Record<string, string> = {
    'Page Shared': 'share',
    // Both share actions report as GA4's recommended `share`; the contest one carries
    // `content_type: 'contest'` so the two stay separable in reporting.
    'Contest Shared': 'share',
    'Page Showtimes and Tickets Opened': 'view_showtimes',
    'Ticket Button Clicked': 'conversion',
    'Page Home Entertainment Clicked': 'view_home_entertainment',
    'Home Entertainment Offer Clicked': 'home_entertainment_offer_click',
    'Page Menu Opened': 'menu_opened',
    'Page Menu Plot Link Clicked': 'menu_plot_clicked',
    'Movie Trailer Played': 'video_start',
    'Watch More Video Opened': 'video_start',
    'Movie Overview Text Expanded': 'overview_expanded',
    'GRETA App Store Clicked': 'greta_app_store_click',
    'GRETA Play Store Clicked': 'greta_play_store_click'
};

/**
 * Facebook Pixel event configuration.
 * Standard events (ViewContent, Lead, etc.) are recognized by Facebook's ad optimization.
 * Custom events use fbq('trackCustom', ...) and are tracked but not used for ad optimization.
 */
type FacebookPixelEventConfig = {
    eventName: string;
    isStandard: boolean;
    mapParams?: (data: Record<string, unknown>) => Record<string, unknown>;
};

export const FB_PIXEL_EVENT_MAP: Record<string, FacebookPixelEventConfig> = {
    'Page Shared': {
        eventName: 'Lead',
        isStandard: true,
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Movie Share'
        })
    },
    'Contest Shared': {
        eventName: 'Lead',
        isStandard: true,
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Contest Share'
        })
    },
    'Page Showtimes and Tickets Opened': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Showtimes',
            content_type: 'product'
        })
    },
    'Ticket Button Clicked': {
        eventName: 'InitiateCheckout',
        isStandard: true,
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Ticket',
            content_type: 'product',
            content_ids: [String(data.showingId as number)]
        })
    },
    'Page Home Entertainment Clicked': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Home Entertainment',
            content_type: 'product'
        })
    },
    'Home Entertainment Offer Clicked': {
        eventName: 'Lead',
        isStandard: true,
        mapParams: (data) => ({
            content_name: (data.offerName as string) || (data.movieTitle as string),
            content_category: 'Home Entertainment Offer'
        })
    },
    'Page Menu Opened': {
        eventName: 'MenuOpened',
        isStandard: false
    },
    'Page Menu Plot Link Clicked': {
        eventName: 'MenuPlotClicked',
        isStandard: false
    },
    'Movie Trailer Played': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Trailer',
            content_type: 'video'
        })
    },
    'Watch More Video Opened': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: (data.videoName as string) || (data.movieTitle as string),
            content_category: 'Watch More Video',
            content_type: 'video'
        })
    },
    'Movie Overview Text Expanded': {
        eventName: 'OverviewExpanded',
        isStandard: false
    },
    'GRETA App Store Clicked': {
        eventName: 'Lead',
        isStandard: true,
        mapParams: () => ({
            content_name: 'GRETA App',
            content_category: 'App Store'
        })
    },
    'GRETA Play Store Clicked': {
        eventName: 'Lead',
        isStandard: true,
        mapParams: () => ({
            content_name: 'GRETA App',
            content_category: 'Play Store'
        })
    }
};

/**
 * TikTok Pixel event configuration.
 * Standard events (ViewContent, Download, etc.) are recognized by TikTok's ad optimization.
 * ttq.track() also accepts custom event names, which are tracked but not used for ad optimization.
 */
type TikTokPixelEventConfig = {
    eventName: string;
    mapParams?: (data: Record<string, unknown>) => Record<string, unknown>;
};

export const TIKTOK_EVENT_MAP: Record<string, TikTokPixelEventConfig> = {
    'Page Shared': {
        eventName: 'Share',
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Movie Share'
        })
    },
    'Contest Shared': {
        eventName: 'Share',
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Contest Share'
        })
    },
    'Page Showtimes and Tickets Opened': {
        eventName: 'ViewContent',
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Showtimes',
            content_type: 'product'
        })
    },
    'Ticket Button Clicked': {
        eventName: 'InitiateCheckout',
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Ticket',
            content_type: 'product',
            content_id: String(data.showingId as number)
        })
    },
    'Page Home Entertainment Clicked': {
        eventName: 'ViewContent',
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Home Entertainment',
            content_type: 'product'
        })
    },
    'Home Entertainment Offer Clicked': {
        eventName: 'ClickButton',
        mapParams: (data) => ({
            content_name: (data.offerName as string) || (data.movieTitle as string),
            content_category: 'Home Entertainment Offer'
        })
    },
    'Page Menu Opened': {
        eventName: 'MenuOpened'
    },
    'Page Menu Plot Link Clicked': {
        eventName: 'MenuPlotClicked'
    },
    'Movie Trailer Played': {
        eventName: 'ViewContent',
        mapParams: (data) => ({
            content_name: data.movieTitle as string,
            content_category: 'Trailer',
            content_type: 'video'
        })
    },
    'Watch More Video Opened': {
        eventName: 'ViewContent',
        mapParams: (data) => ({
            content_name: (data.videoName as string) || (data.movieTitle as string),
            content_category: 'Watch More Video',
            content_type: 'video'
        })
    },
    'Movie Overview Text Expanded': {
        eventName: 'OverviewExpanded'
    },
    'GRETA App Store Clicked': {
        eventName: 'Download',
        mapParams: () => ({
            content_name: 'GRETA App',
            content_category: 'App Store'
        })
    },
    'GRETA Play Store Clicked': {
        eventName: 'Download',
        mapParams: () => ({
            content_name: 'GRETA App',
            content_category: 'Play Store'
        })
    }
};

/**
 * Reddit Ads pixel event configuration.
 * Reddit only recognises a fixed set of standard event names (PageVisit, ViewContent, Search,
 * AddToCart, AddToWishlist, Purchase, Lead, SignUp) for ad optimisation. Anything else has to
 * go through the `Custom` event with a `customEventName`, which is tracked but not optimised.
 */
type RedditPixelEventConfig = {
    eventName: string;
    mapParams?: (data: Record<string, unknown>) => Record<string, unknown>;
};

export const REDDIT_EVENT_MAP: Record<string, RedditPixelEventConfig> = {
    'Page Shared': {
        eventName: 'Lead'
    },
    'Contest Shared': {
        eventName: 'Lead'
    },
    'Page Showtimes and Tickets Opened': {
        eventName: 'ViewContent'
    },
    // Reddit has no InitiateCheckout; AddToCart is the closest optimisable event to a ticket
    // click, since the purchase itself completes on the cinema's ticketing system.
    'Ticket Button Clicked': {
        eventName: 'AddToCart'
    },
    'Page Home Entertainment Clicked': {
        eventName: 'ViewContent'
    },
    'Home Entertainment Offer Clicked': {
        eventName: 'Lead'
    },
    'Movie Trailer Played': {
        eventName: 'ViewContent'
    },
    'Watch More Video Opened': {
        eventName: 'ViewContent'
    },
    'GRETA App Store Clicked': {
        eventName: 'Lead'
    },
    'GRETA Play Store Clicked': {
        eventName: 'Lead'
    }
};

/**
 * Convert an event name to snake_case for GA4 fallback.
 * E.g., "Some New Event" → "some_new_event"
 */
export function toSnakeCase(str: string): string {
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
}
